<template>
	<UserPageLayout :loading="loading" :error="error">
		<table class="generic-table">
			<thead>
				<tr>
					<th>Login</th>
					<th>Password</th>
					<th>Created</th>
					<th>Actions</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="socks in socksList" :key="socks.login">
					<td>{{ socks.login }}</td>
					<td>{{ socks.password }}</td>
					<td>{{ $formatDate(socks.created) }}</td>
					<td>
						<div class="generic-table-actions">
							<button 
								@click="deleteSocks(socks.login)"
								class="generic-table-action-button"
								:disabled="action_loading"
							>
								Delete
							</button>
						</div>
					</td>
				</tr>
				<tr>
					<td>
						<input 
							v-model="newLogin" 
							type="text" 
							:placeholder="`Login ${loginRegex} (${minLoginLength}-${maxLoginLength})`"
							:minlength="minLoginLength"
							:maxlength="maxLoginLength"
							@input="filterLoginInput"
							@keyup.enter="addSocks"
							:disabled="action_loading"
						/>
					</td>
					<td>
						<input 
							v-model="newPassword" 
							type="password" 
							placeholder="Password"
							@keyup.enter="addSocks"
							:disabled="action_loading"
						/>
					</td>
					<td></td>
					<td>
						<button 
							@click="addSocks"
							class="generic-table-action-button"
							:disabled="action_loading || !loginMeetsLengthRules || !newPassword.trim()"
						>
							Add
						</button>
					</td>
				</tr>
			</tbody>
		</table>
	</UserPageLayout>
</template>

<script>
import UserPageLayout from './_PageLayout.vue'

export default {
	name: 'UserSocks',
	components: {
		UserPageLayout
	},
	data() {
		return {
			loading: true,
			action_loading: false,
			error: '',
			socksList: [],
			newLogin: '',
			newPassword: '',
			loginRegex: '',
			minLoginLength: 1,
			maxLoginLength: 256
		}
	},
	computed: {
		loginMeetsLengthRules() {
			const len = this.newLogin.trim().length
			return len >= this.minLoginLength && len <= this.maxLoginLength
		}
	},
	async mounted() {
		await this.fetchSocks()
	},
	methods: {
		async fetchSocks() {
			this.loading = true
			this.error = ''

			const authToken = localStorage.getItem('authToken')?.trim()
			if (!authToken || authToken === 'null') {
				this.$router.push({ name: 'Login' })
				return
			}
			
			try {
				const response = await this.$api.post('/api/user/socks', {
					authToken: authToken
				})
				
				this.socksList = response.data.socks || []
				this.loginRegex = response.data.login_regex
				this.minLoginLength = response.data.min_login_length
				this.maxLoginLength = response.data.max_login_length
			} catch (error) {
				if (error.response && error.response.status === this.$StatusCodes.UNAUTHORIZED) {
					localStorage.removeItem('authToken')
					this.$router.push({ name: 'Login' })
				} else {
					this.error = 'An error occurred while loading SOCKS accounts.'
					console.error('Error:', error)
				}
			} finally {
				this.loading = false
			}
		},
		filterLoginInput(event) {
			const newValue = event.target.value
			try {
				const regex = new RegExp(this.loginRegex, 'g')
				const match = newValue.match(regex)
				const validValue = match ? match.join('') : ''
				this.newLogin = validValue
			} catch (e) {
				// If regex is invalid, don't filter
				this.newLogin = newValue
			}
		},
		async addSocks() {
			if (!this.newLogin.trim() || !this.newPassword.trim()) {
				return
			}

			this.action_loading = true
			this.error = ''
			const authToken = localStorage.getItem('authToken')?.trim()

			try {
				await this.$api.post('/api/user/socks/add', {
					authToken: authToken,
					login: this.newLogin.trim(),
					password: this.newPassword.trim()
				})
				this.newLogin = ''
				this.newPassword = ''
				await this.fetchSocks()
			} catch (error) {
				if (error.response && error.response.status === this.$StatusCodes.UNAUTHORIZED) {
					localStorage.removeItem('authToken')
					this.$router.push({ name: 'Login' })
				} else if (error.response && error.response.status === this.$StatusCodes.BAD_REQUEST) {
					this.error = 'Maximum SOCKS accounts reached.'
				} else if (error.response && error.response.status === this.$StatusCodes.CONFLICT) {
					this.error = 'A SOCKS account with this login already exists.'
				} else if (error.response && error.response.status === this.$StatusCodes.FORBIDDEN) {
					this.error = 'Password must be at least 8 characters long, not dictionary and not the same as user password.'
				} else {
					this.error = 'An error occurred while adding SOCKS account.'
					console.error('Error:', error)
				}
			} finally {
				this.action_loading = false
			}
		},
		async deleteSocks(login) {
			this.action_loading = true
			this.error = ''
			const authToken = localStorage.getItem('authToken')?.trim()

			try {
				await this.$api.post('/api/user/socks/delete', {
					authToken: authToken,
					login: login
				})
				await this.fetchSocks()
			} catch (error) {
				if (error.response && error.response.status === this.$StatusCodes.UNAUTHORIZED) {
					localStorage.removeItem('authToken')
					this.$router.push({ name: 'Login' })
				} else if (error.response && error.response.status === this.$StatusCodes.NOT_FOUND) {
					this.error = 'SOCKS account not found.'
				} else {
					this.error = 'An error occurred while deleting SOCKS account.'
					console.error('Error:', error)
				}
			} finally {
				this.action_loading = false
			}
		}
	}
}
</script>

<style scoped>
input {
	width: 100%;
	padding: 8px;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 14px;
	box-sizing: border-box;
	line-height: 1.5;
	min-width: 200px;
}

input::placeholder {
	font-weight: bold;
	font-size: 12px;
}

</style>

